import { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, Search, X } from "lucide-react";
import api from "@/utils/api";
import { ROLE_LABELS } from "@/utils/format";

const ROLES = ["admin", "satis_destek", "muhasebe", "depo", "b2b_musteri"];
const EMPTY_FORM = {
  email: "", password: "", name: "", role: "satis_destek", cari_id: "", is_active: true,
};

const INPUT_CLS =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors";
const LABEL_CLS =
  "block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1";

// ─── Searchable Cari Selector ───────────────────────────────────────────────
// IMPORTANT: defined OUTSIDE UserModal to prevent remount/focus loss.
function SearchableCariSelect({ initialCariId, onChange }) {
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const ref                   = useRef(null);

  // When editing an existing user, pre-fill the display name by fetching the cari
  useEffect(() => {
    if (!initialCariId) { setQuery(""); return; }
    api.get(`/cariler/${initialCariId}`)
      .then((r) => setQuery(`${r.data.kod} — ${r.data.unvan}`))
      .catch(() => setQuery(initialCariId)); // fallback: show raw id
  }, [initialCariId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounced API search — fires 300ms after user stops typing
  useEffect(() => {
    if (query.length < 1) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/cariler/", { params: { search: query, per_page: 15 } });
        setResults(r.data.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (cari) => {
    setQuery(`${cari.kod} — ${cari.unvan}`);
    setResults([]);
    setOpen(false);
    onChange(cari.id);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange("");
  };

  return (
    <div ref={ref} className="relative">
      {/* Input */}
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          autoComplete="off"
          placeholder="Cari kodu veya ünvan yazın..."
          className={`${INPUT_CLS} pl-8 pr-8`}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange("");
          }}
          onFocus={() => query.length > 0 && setOpen(true)}
        />
        {query && (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && query.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="max-h-52 overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="px-4 py-3 text-xs text-slate-400">Aranıyor...</div>
            ) : results.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400">Sonuç bulunamadı</div>
            ) : results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelect(c)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0 transition-colors"
              >
                <span className="font-mono text-xs text-slate-500 shrink-0 w-28 truncate">{c.kod}</span>
                <span className="truncate text-slate-700 flex-1">{c.unvan}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── UserModal ───────────────────────────────────────────────────────────────
export function UserModal({ isOpen, onClose, editItem, onSuccess }) {
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setForm(editItem ? { ...EMPTY_FORM, ...editItem, password: "" } : EMPTY_FORM);
    setShowPassword(false);
  }, [editItem, isOpen]);

  if (!isOpen) return null;

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSuccess(form, editItem);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-bold text-[#0D1B3E]">
            {editItem ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">

            {/* Ad Soyad */}
            <div>
              <label className={LABEL_CLS}>Ad Soyad *</label>
              <input type="text" value={form.name || ""} required onChange={set("name")} className={INPUT_CLS} />
            </div>

            {/* E-Posta */}
            <div>
              <label className={LABEL_CLS}>{editItem ? "E-Posta" : "E-Posta *"}</label>
              <input type="email" value={form.email || ""} required={!editItem} onChange={set("email")} className={INPUT_CLS} />
            </div>

            {/* Şifre — göz ikonu ile görünürlük toggle */}
            <div>
              <label className={LABEL_CLS}>{editItem ? "Şifre (boş = değişmez)" : "Şifre *"}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password || ""}
                  required={!editItem}
                  onChange={set("password")}
                  autoComplete="new-password"
                  className={`${INPUT_CLS} pr-10`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0D1B3E] transition-colors"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {/* Rol */}
            <div>
              <label className={LABEL_CLS}>Rol *</label>
              <select value={form.role} onChange={set("role")} className={`${INPUT_CLS} bg-white`}>
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>

          </div>

          {/* Cari Eşleme — sadece b2b_musteri; arama destekli */}
          {form.role === "b2b_musteri" && (
            <div>
              <label className={LABEL_CLS}>
                Cari Eşleme
                <span className="ml-1.5 text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                  (tüm cariler aranabilir)
                </span>
              </label>
              <SearchableCariSelect
                initialCariId={form.cari_id || ""}
                onChange={(id) => setForm((p) => ({ ...p, cari_id: id }))}
              />
            </div>
          )}

          {/* Butonlar */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
              İptal
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
